import React, { useCallback, useEffect, useState } from 'react';
import { BackPill } from './BackPill';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '../../utils/api';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { EmojiArt } from '../EmojiArt';
import { deckForRound, CATEGORY_ORDER, _DECKS_INTERNAL as DECKS } from '../../data/blissEchoDecks';
import * as HarmonySFX from '../../utils/harmonySounds';
import { vibrate as nativeVibrate } from '../../utils/haptics';

/* ═══════════════════════════════════════════════════════════════════
   THE HARMONY — 5×5 alignment puzzle powered by category-cycling
   emoji icons (same 10 categories × 10 cycles as Echo).
   Round counter persists in localStorage. Reshuffle keeps the board;
   Next Round advances to the next category.
   ═══════════════════════════════════════════════════════════════════ */

const GOLD    = '#D4AF37';
const GOLD_HI = '#F5D97A';
const COLS = 5;
const ROWS = 7;
const TILES = COLS * ROWS;      // 35 tiles
const ICON_COUNT = ROWS;        // 7 distinct icons × 5 copies each = 35
const MATCH_LEN = COLS;         // 5 in a horizontal row (fills the whole row)
const LUCKY_BIAS_AFTER = 4;
const ROUND_KEY  = 'harmony_round_idx';
const USED_KEY   = 'harmony_used_by_cat';   // { catKey: [emoji,…] emojis already shown in past rounds }
const ROUND_ICONS_KEY = 'harmony_round_icons'; // { [roundIdx]: [emoji,…] sticky current-round icons }

/* Per-tile hue palette — each of the 6 unique icons in a round is
   assigned a distinct jewel-tone so users can differentiate at a glance.
   No two adjacent hues on the color wheel to maximize contrast. */
const TILE_HUES = [
  { base: '#7a1428', glow: '#FF7A8A' },   // Ruby
  { base: '#0f5236', glow: '#3DDC97' },   // Emerald
  { base: '#183a7a', glow: '#8FB4FF' },   // Sapphire
  { base: '#3f1f66', glow: '#C39CFF' },   // Amethyst
  { base: '#6b3e18', glow: '#F5D97A' },   // Amber
  { base: '#173561', glow: '#5FAAFF' },   // Cobalt
];

const readRound = () => {
  try {
    const v = parseInt(window.localStorage.getItem(ROUND_KEY) || '0', 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  } catch (_) { return 0; /* localStorage blocked */ }
};
const writeRound = (n) => {
  try { window.localStorage.setItem(ROUND_KEY, String(n)); } catch (_) { /* noop */ }
};

/* Unique-emoji tracker — per category, remember which emojis have already been
   used across previous Harmony rounds. When picking a new round we DRAW from the
   fresh pool only; if a category's fresh pool is exhausted we reset it.
   IMPORTANT: readUsed / readRoundIcons are PURE (read-only). All writes go
   through explicit commit functions called from event handlers, never render. */
const readUsed = () => {
  try {
    const raw = window.localStorage.getItem(USED_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) { return {}; }
};
const writeUsed = (map) => {
  try { window.localStorage.setItem(USED_KEY, JSON.stringify(map)); } catch (_) { /* noop */ }
};
const readRoundIcons = () => {
  try {
    const raw = window.localStorage.getItem(ROUND_ICONS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch (_) { return {}; }
};
const writeRoundIcons = (map) => {
  try { window.localStorage.setItem(ROUND_ICONS_KEY, JSON.stringify(map)); } catch (_) { /* noop */ }
};

/* All unique emojis available for a category — flatten every cycle and dedupe. */
const uniquePoolForCategory = (catKey) => {
  const cycles = DECKS[catKey] || [];
  const seen = new Set();
  cycles.forEach((c) => c.forEach((e) => seen.add(e)));
  return Array.from(seen);
};

/* PURE — pick `count` emojis for `catKey` that aren't in `usedSet`.
   Returns { picks, usedResetTo } where usedResetTo is the new used array
   for that category (either extended with picks, or reset to [picks]
   if the fresh pool was exhausted). No side effects. */
const pickFreshIconsPure = (catKey, count, usedSetInput) => {
  const pool = uniquePoolForCategory(catKey);
  let usedArr = Array.from(usedSetInput || []);
  let fresh = pool.filter((e) => !usedArr.includes(e));
  let reset = false;
  if (fresh.length < count) {
    // Exhausted — start a new cycle for this category. Bias picks away from
    // the most-recently-used set so we don't immediately re-show the same
    // emojis on the reset round.
    const avoid = new Set(usedArr.slice(-count));
    let candidate = pool.filter((e) => !avoid.has(e));
    if (candidate.length < count) candidate = pool.slice();
    fresh = candidate;
    usedArr = [];
    reset = true;
  }
  // Fisher-Yates over fresh
  const a = fresh.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  const picks = a.slice(0, count);
  const usedResetTo = reset ? picks.slice() : [...usedArr, ...picks];
  return { picks, usedResetTo };
};

/* SIDE-EFFECTFUL — must only be called from an event handler / effect.
   Picks fresh icons for the given round & category and commits state to
   localStorage (both the used-map for the category AND the sticky round icons). */
const commitPickForRound = (roundIdx, catKey, count) => {
  const usedMap = readUsed();
  const { picks, usedResetTo } = pickFreshIconsPure(catKey, count, usedMap[catKey] || []);
  usedMap[catKey] = usedResetTo;
  writeUsed(usedMap);
  const roundIcons = readRoundIcons();
  roundIcons[String(roundIdx)] = picks;
  writeRoundIcons(roundIcons);
  return picks;
};

/* Pure — returns the sticky icons already stored for a round, or null. */
const getStickyRoundIcons = (roundIdx) => {
  const roundIcons = readRoundIcons();
  const v = roundIcons[String(roundIdx)];
  return Array.isArray(v) && v.length > 0 ? v : null;
};

const shuffleArr = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const detect = (flat) => {
  // Horizontal-only: MATCH_LEN consecutive same tiles anywhere in a row of COLS.
  // Store as { row, start } so we can highlight only the aligned cells.
  const rows = [];
  for (let r = 0; r < ROWS; r++) {
    for (let start = 0; start + MATCH_LEN <= COLS; start++) {
      const first = flat[r * COLS + start];
      if (!first) continue;
      let ok = true;
      for (let k = 1; k < MATCH_LEN; k++) {
        if (flat[r * COLS + start + k] !== first) { ok = false; break; }
      }
      if (ok) { rows.push({ row: r, start }); break; }
    }
  }
  // Column matches are disabled — this is a strictly horizontal alignment ritual.
  return { rows, cols: [] };
};

const isNeighbor = (a, b) => {
  const ra = Math.floor(a / COLS), ca = a % COLS;
  const rb = Math.floor(b / COLS), cb = b % COLS;
  return (ra === rb && Math.abs(ca - cb) === 1) || (ca === cb && Math.abs(ra - rb) === 1);
};

export const TheHarmony = ({ onClose }) => {
  const [roundIdx, setRoundIdx] = useState(readRound());
  const [grid, setGrid] = useState([]);
  const [aligned, setAligned] = useState({ rows: [], cols: [] });
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [swapCount, setSwapCount] = useState(0);
  const [shuffleCount, setShuffleCount] = useState(0);
  const [resolvedCount, setResolvedCount] = useState(0);
  const [unsuccessfulStreak, setUnsuccessfulStreak] = useState(0);
  const [resonance, setResonance] = useState(null);
  const [animateKey, setAnimateKey] = useState(0);
  const [cycleComplete, setCycleComplete] = useState(false);

  const deck = deckForRound(roundIdx);
  // Icon selection is STICKY per roundIdx — if this round already has icons
  // stored we reuse them (idempotent), otherwise we commit fresh picks once.
  // No side-effects during render.
  const [iconSet, setIconSet] = useState(() => {
    const sticky = getStickyRoundIcons(roundIdx);
    return sticky || [];
  });
  useEffect(() => {
    // Runs exactly once per new roundIdx. If icons already sticky we skip.
    if (iconSet.length > 0) return;
    const picks = commitPickForRound(roundIdx, deck.id, ICON_COUNT);
    setIconSet(picks);
  }, [roundIdx]);
  const hueByIcon = React.useMemo(() => {
    const m = {};
    iconSet.forEach((k, i) => { m[k] = TILE_HUES[i % TILE_HUES.length]; });
    return m;
  }, [iconSet]);

  useHistoryClose(true, useCallback(() => { onClose(); }, [onClose]));

  const buildPool = useCallback((icons) => icons.flatMap((k) => Array(COLS).fill(k)), []);

  const performShuffle = useCallback((forceAlign = false, icons = iconSet) => {
    const useBias = forceAlign || unsuccessfulStreak >= LUCKY_BIAS_AFTER;
    const pool = buildPool(icons);
    let arr;
    if (useBias) {
      const target = icons[Math.floor(Math.random() * icons.length)];
      // Horizontal-only alignment forcing — plant MATCH_LEN identical tiles
      // starting at a random valid column window within a random row.
      const rowIdx = Math.floor(Math.random() * ROWS);
      const startCol = Math.floor(Math.random() * (COLS - MATCH_LEN + 1));
      const linePositions = Array.from({ length: MATCH_LEN }, (_, i) => rowIdx * COLS + startCol + i);
      let remaining = pool.slice();
      for (let i = 0; i < linePositions.length; i++) {
        const idx = remaining.indexOf(target);
        if (idx >= 0) remaining.splice(idx, 1);
      }
      remaining = shuffleArr(remaining);
      arr = Array(TILES).fill(null);
      linePositions.forEach((p) => { arr[p] = target; });
      let fill = 0;
      for (let i = 0; i < TILES; i++) if (arr[i] === null) arr[i] = remaining[fill++];
    } else {
      arr = shuffleArr(pool);
    }
    setGrid(arr);
    setAnimateKey((k) => k + 1);
    setSelectedIdx(null);
    setSwapCount(0);
    setCycleComplete(false);
    const found = detect(arr);
    setAligned(found);
    setShuffleCount((c) => c + 1);
    const hasAny = found.rows.length + found.cols.length > 0;
    setUnsuccessfulStreak((s) => hasAny ? 0 : s + 1);
    HarmonySFX.shuffle();
    if (hasAny) {
      HarmonySFX.align();
      setResonance({ key: Date.now(), text: 'RESONANCE FOUND' });
      setTimeout(() => setResonance(null), 1800);
      try { nativeVibrate([60, 30, 100]); } catch (_) { /* noop */ }
    } else {
      try { nativeVibrate(25); } catch (_) { /* noop */ }
    }
  }, [iconSet, unsuccessfulStreak, buildPool]);

  const recomputeAlignments = useCallback((flat) => {
    const found = detect(flat);
    /* Play the align chime ONLY for rows that just became aligned this
       swap — not for rows that were already aligned in the previous
       state. This prevents the old bottom-row match sound from firing
       every time the user swaps somewhere else. */
    setAligned((prev) => {
      const prevKeys = new Set((prev.rows || []).map((m) => `${m.row}:${m.start}`));
      const newly = (found.rows || []).filter((m) => !prevKeys.has(`${m.row}:${m.start}`));
      if (newly.length > 0) {
        HarmonySFX.align();
        setResonance({ key: Date.now(), text: 'RESONANCE FOUND' });
        setTimeout(() => setResonance(null), 1800);
        try { nativeVibrate([60, 30, 100]); } catch (_) { /* noop */ }
      }
      if (found.rows.length === ROWS) {
        setCycleComplete(true);
        HarmonySFX.resolve();
        try { nativeVibrate([120, 60, 120, 60, 240]); } catch (_) { /* noop */ }
      }
      return found;
    });
    return found.rows.length > 0;
  }, []);

  const handleTileTap = (idx) => {
    if (selectedIdx === null) { setSelectedIdx(idx); HarmonySFX.select(); return; }
    if (selectedIdx === idx)  { setSelectedIdx(null); return; }
    if (!isNeighbor(selectedIdx, idx)) {
      setSelectedIdx(idx);
      HarmonySFX.select();
      try { nativeVibrate(15); } catch (_) { /* noop */ }
      return;
    }
    setGrid((prev) => {
      const next = prev.slice();
      [next[selectedIdx], next[idx]] = [next[idx], next[selectedIdx]];
      recomputeAlignments(next);
      return next;
    });
    setSwapCount((n) => n + 1);
    setSelectedIdx(null);
    HarmonySFX.swap();
    try { nativeVibrate(20); } catch (_) { /* noop */ }
  };

  // First board on mount / whenever iconSet changes to a fresh set.
  useEffect(() => {
    if (iconSet.length === ICON_COUNT && grid.length === 0) {
      performShuffle(false, iconSet);
    }
  }, [iconSet, grid.length]);

  const hasAlignment = aligned.rows.length > 0 || aligned.cols.length > 0;

  const resolveAlignment = () => {
    if (!hasAlignment) return;
    const rowKey = aligned.rows.map((m) => `${m.row}:${m.start}`).join(',');
    const colKey = aligned.cols.map((m) => `${m.col}:${m.start}`).join(',');
    const id = `harmony_r${rowKey}_c${colKey}_${Date.now()}`;
    axios.post(`${BACKEND_URL}/api/provenance/progress`, { game: 'harmony', item_id: id }).catch(() => {});
    setResolvedCount((r) => r + 1);
    HarmonySFX.resolve();
    setResonance({ key: Date.now(), text: 'HARMONY ACHIEVED' });
    setTimeout(() => setResonance(null), 2000);
    try { nativeVibrate([100, 50, 100, 50, 200]); } catch (_) { /* noop */ }
    setTimeout(() => performShuffle(false, iconSet), 1800);
  };

  const isInAlignedLine = (idx) => {
    const r = Math.floor(idx / COLS);
    const c = idx % COLS;
    if ((aligned.rows || []).some((m) => m.row === r && c >= m.start && c < m.start + MATCH_LEN)) return true;
    return (aligned.cols || []).some((m) => m.col === c && r >= m.start && r < m.start + MATCH_LEN);
  };

  const changeCategory = () => {
    const nextIdx = roundIdx + 1;
    writeRound(nextIdx);
    const nextDeck = deckForRound(nextIdx);
    // Reuse sticky icons if this round has been visited before, else commit
    // a fresh pick that excludes already-used emojis for that category.
    const nextIcons = getStickyRoundIcons(nextIdx)
      || commitPickForRound(nextIdx, nextDeck.id, ICON_COUNT);
    setRoundIdx(nextIdx);
    setIconSet(nextIcons);
    setResolvedCount(0);
    setUnsuccessfulStreak(0);
    setCycleComplete(false);
    setGrid([]);       // forces the [iconSet]-effect to re-shuffle with new icons
  };

  return (
    <div className="fixed inset-0 z-[220] flex flex-col"
      style={{
        background:
          'repeating-linear-gradient(135deg, rgba(212,175,55,0.018) 0px, rgba(212,175,55,0.018) 2px, transparent 2px, transparent 18px),' +
          'radial-gradient(ellipse at top, #1a1024 0%, #050308 75%)',
      }}
      data-testid="harmony-modal">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0"
        style={{ borderColor: `${GOLD}22` }}>
        <BackPill onClick={onClose} accent="#8A80F0" testId="harmony-back"/>
        <p className="font-['Cinzel'] tracking-[0.24em]"
          style={{ color: '#FFE9A0', fontSize: 13, textShadow: `0 0 10px ${GOLD}55` }}>
          HARMONY · {deck.label.toUpperCase()}
        </p>
        <span className="w-6"/>
      </div>

      {/* Body — heading pinned to top, buttons to bottom, board centered */}
      <div className="relative flex-1 flex flex-col items-center px-3 py-2 min-h-0 overflow-hidden">
        <p className="font-['Cormorant_Garamond'] italic text-center text-white/65 text-[14px]">
          Arrange 5 identical tiles in one horizontal row.
        </p>
        <div className="flex items-center gap-3 mt-1.5 font-['Outfit'] text-[10px] uppercase tracking-[0.22em]"
          data-testid="harmony-stats">
          <span className="text-white/40">Shuffles: <span style={{ color: GOLD }}>{shuffleCount}</span></span>
          <span className="opacity-40">·</span>
          <span className="text-white/40">Swaps: <span style={{ color: GOLD }}>{swapCount}</span></span>
          <span className="opacity-40">·</span>
          <span className="text-white/40">Resolved: <span style={{ color: GOLD }}>{resolvedCount}</span></span>
        </div>
        <p className="font-['Outfit'] text-[9px] uppercase tracking-[0.22em] mt-1"
          style={{ color: selectedIdx !== null ? GOLD_HI : 'rgba(255,255,255,0.28)', minHeight: 12 }}>
          {selectedIdx !== null
            ? '✦ Tap an adjacent tile to swap'
            : 'Tap two adjacent tiles to swap · align 5 same tiles horizontally'}
        </p>

        {/* Board fills the remaining vertical space and clamps to viewport width.
            Wrapped in the same ornate gold picture-frame used by Provenance —
            gold-hairline border, corner filigree, and a subtle bloom on alignment. */}
        <div className="relative my-2 flex-1 flex items-center justify-center w-full min-h-0">
          <div style={{
            position: 'relative',
            /* Portrait 5×7 board — width is derived from both viewport width AND
               available vertical height so the whole grid + buttons fit on screen. */
            width: 'min(88vw, calc((100dvh - 260px) * 5 / 7), 380px)',
            padding: 'clamp(6px, 2vw, 12px)',
            borderRadius: 14,
            background:
              `linear-gradient(140deg, rgba(255,233,160,0.10) 0%, rgba(212,175,55,0.04) 50%, rgba(139,107,31,0.08) 100%),
               linear-gradient(180deg, #120818 0%, #05030a 100%)`,
            border: `1.5px solid ${GOLD}`,
            boxShadow: hasAlignment
              ? `0 0 42px ${GOLD}aa, 0 0 90px ${GOLD}55, inset 0 0 22px ${GOLD}44`
              : `0 0 22px rgba(0,0,0,0.75), 0 0 0 2px ${GOLD}22, inset 0 0 14px rgba(212,175,55,0.08)`,
            transition: 'box-shadow 900ms ease',
          }}>
            {/* Corner filigree — four gold L-brackets */}
            {[
              { top: 4,    left: 4,    rot: 0   },
              { top: 4,    right: 4,   rot: 90  },
              { bottom: 4, right: 4,   rot: 180 },
              { bottom: 4, left: 4,    rot: 270 },
            ].map((c, i) => (
              <svg key={i} width="22" height="22" viewBox="0 0 22 22"
                style={{
                  position: 'absolute',
                  ...c,
                  transform: `rotate(${c.rot}deg)`,
                  pointerEvents: 'none',
                  filter: `drop-shadow(0 0 4px ${GOLD}88)`,
                  zIndex: 3,
                }} aria-hidden>
                <path d="M2 8 L2 2 L8 2" fill="none" stroke={GOLD_HI} strokeWidth="1.4" strokeLinecap="round"/>
                <circle cx="2" cy="2" r="1.6" fill={GOLD}/>
              </svg>
            ))}

            <div key={animateKey} className="grid gap-1"
              style={{
                gridTemplateColumns: `repeat(${COLS}, minmax(0, 1fr))`,
                gridAutoRows: '1fr',
                width: '100%',
                aspectRatio: `${COLS} / ${ROWS}`,
                animation: 'harmonyBoardIn 0.5s ease-out',
              }}
              data-testid="harmony-grid">
            {grid.map((emoji, i) => {
              const inLine = isInAlignedLine(i);
              const isSelected = selectedIdx === i;
              const isNeighborTarget = selectedIdx !== null && selectedIdx !== i && isNeighbor(selectedIdx, i);
              const hue = hueByIcon[emoji] || TILE_HUES[0];
              return (
                <button key={i}
                  type="button"
                  onClick={() => handleTileTap(i)}
                  className="relative overflow-hidden flex items-center justify-center p-0 m-0"
                  style={{
                    aspectRatio: '1 / 1',
                    borderRadius: 10,
                    border: isSelected
                      ? `2px solid ${GOLD_HI}`
                      : isNeighborTarget
                        ? `1.5px solid ${GOLD}cc`
                        : inLine ? `1.5px solid ${GOLD_HI}` : `1px solid ${GOLD}30`,
                    /* Uniform obsidian tile background (colors live on the emoji glow only). */
                    background: inLine
                      ? `radial-gradient(circle at 30% 25%, ${GOLD}44 0%, rgba(20,8,28,0.95) 75%)`
                      : 'radial-gradient(circle at 30% 25%, rgba(40,28,55,0.85) 0%, rgba(8,4,16,0.95) 80%)',
                    boxShadow: isSelected
                      ? `0 0 22px ${GOLD_HI}, 0 0 40px ${GOLD}88, inset 0 0 14px ${GOLD}66`
                      : isNeighborTarget
                        ? `0 0 12px ${GOLD}aa, inset 0 0 8px ${GOLD}33`
                        : inLine
                          ? `0 0 18px ${GOLD}cc, 0 0 36px ${GOLD}55, inset 0 0 12px ${GOLD}33`
                          : `inset 0 0 6px rgba(0,0,0,0.6), 0 1px 2px rgba(0,0,0,0.5)`,
                    transform: isSelected ? 'translateZ(0) scale(0.94)' : 'translateZ(0) scale(1)',
                    transition: 'transform 380ms cubic-bezier(0.22, 1, 0.36, 1), border-color 320ms ease, box-shadow 320ms ease',
                    zIndex: isSelected ? 5 : 1,
                  }}
                  data-testid={`harmony-tile-${i}`}
                  data-aligned={inLine ? 'true' : 'false'}
                  aria-label={`Tile ${i + 1}${inLine ? ' (aligned)' : ''}`}>
                  {/* Inner glass sheen — premium curved highlight */}
                  <span aria-hidden="true"
                    style={{
                      position: 'absolute', top: 2, left: 2, right: 2, height: '38%',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.14) 0%, transparent 100%)',
                      borderRadius: '9px 9px 40px 40px',
                      pointerEvents: 'none',
                    }}/>
                  <span style={{
                    width: 'clamp(40px, 13vw, 70px)', height: 'clamp(40px, 13vw, 70px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    lineHeight: 1,
                    /* No glow — clean crystal-clear emoji rendering. Keep a
                       tiny saturation lift only when aligned so it stands out. */
                    filter: inLine ? 'saturate(1.15)' : 'none',
                    transition: 'filter 400ms ease',
                    position: 'relative',
                    zIndex: 2,
                  }}>
                    <EmojiArt emoji={emoji} size="100%" glow={inLine}/>
                  </span>
                </button>
              );
            })}
          </div>
          </div>

          <AnimatePresence>
            {resonance && (
              <motion.div key={resonance.key}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 1.1 }}
                transition={{ duration: 0.45 }}
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                data-testid="harmony-toast">
                <div className="px-5 py-2 rounded-full"
                  style={{
                    background: 'linear-gradient(140deg, #1a1024, #0a0612)',
                    border: `1.5px solid ${GOLD}`,
                    boxShadow: `0 0 30px ${GOLD}cc, 0 0 60px ${GOLD}66`,
                  }}>
                  <span className="font-['Cinzel'] text-[12px] tracking-[0.3em]" style={{ color: GOLD_HI }}>
                    ✦ {resonance.text}
                  </span>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Action row */}
        <div className="flex gap-2.5 mt-2 flex-wrap justify-center">
          <button onClick={() => performShuffle(false, iconSet)}
            className="px-4 py-2 rounded-full font-['Cinzel'] uppercase tracking-[0.22em] text-[10px]"
            style={{ border: `1.5px solid ${GOLD}`, color: GOLD, background: 'rgba(212,175,55,0.06)' }}
            data-testid="harmony-shuffle">
            ↻ Shuffle
          </button>
          <button onClick={changeCategory}
            className="px-4 py-2 rounded-full font-['Cinzel'] uppercase tracking-[0.22em] text-[10px]"
            style={{ border: `1.5px solid ${GOLD}66`, color: GOLD_HI, background: 'rgba(212,175,55,0.04)' }}
            data-testid="harmony-change">
            → Next Set
          </button>
          {hasAlignment && (
            <motion.button onClick={resolveAlignment}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: 'spring', stiffness: 260, damping: 16 }}
              className="px-4 py-2 rounded-full font-['Cinzel'] uppercase tracking-[0.22em] text-[10px]"
              style={{
                background: `linear-gradient(140deg, ${GOLD_HI} 0%, ${GOLD} 60%, #8B6B1F 100%)`,
                color: '#0a0610',
                boxShadow: `0 0 22px ${GOLD}, 0 0 44px ${GOLD}66`,
              }}
              data-testid="harmony-resolve">
              ✦ Resolve
            </motion.button>
          )}
        </div>

        {!hasAlignment && unsuccessfulStreak >= LUCKY_BIAS_AFTER - 1 && (
          <p className="mt-1.5 font-['Cormorant_Garamond'] italic text-[12px] text-white/35">
            The realm stirs… an alignment draws near.
          </p>
        )}
        <p className="font-['Outfit'] uppercase tracking-[0.22em] mt-1.5"
          style={{ color: 'rgba(255,255,255,0.28)', fontSize: 8,
            paddingBottom: 'max(var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)), 6px)' }}>
          {CATEGORY_ORDER.length} categories · cycles forever
        </p>

        <style>{`
          @keyframes harmonyPulse {
            0%, 100% { box-shadow: 0 0 14px ${GOLD}99, 0 0 28px ${GOLD}44, inset 0 0 8px ${GOLD}22; }
            50%      { box-shadow: 0 0 22px ${GOLD_HI}, 0 0 52px ${GOLD}77, inset 0 0 18px ${GOLD}55; }
          }
          @keyframes harmonyBoardIn {
            from { opacity: 0; transform: translate3d(0, 8px, 0) scale(0.97); }
            to   { opacity: 1; transform: translate3d(0, 0, 0)   scale(1); }
          }
        `}</style>

        {/* Cycle complete overlay — appears the instant every row is
            aligned so the user can advance without hunting for a button.
            Centred (matching the Echo/Provenance overlay style). */}
        {cycleComplete && (
          <div className="absolute inset-0 z-[10] flex items-center justify-center px-4"
            style={{ background: 'radial-gradient(ellipse at center, rgba(5,3,8,0.85) 0%, rgba(5,3,8,0.98) 70%)' }}
            data-testid="harmony-complete-overlay">
            <motion.div
              initial={{ opacity: 0, scale: 0.85, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 220, damping: 18 }}
              className="w-full max-w-md rounded-3xl p-6 text-center"
              style={{
                background: 'radial-gradient(ellipse at top, #1a1024 0%, #050308 90%)',
                border: `1.5px solid ${GOLD}`,
                boxShadow: `0 0 32px ${GOLD}77, inset 0 0 22px ${GOLD}22`,
              }}>
              <p className="font-['Outfit'] uppercase tracking-[0.28em] text-[10px]"
                style={{ color: `${GOLD}CC` }}>
                {deck.label} · CYCLE {String.fromCharCode(65 + (roundIdx % 26))}
              </p>
              <h2 className="font-['Cinzel'] tracking-[0.24em] mt-3"
                style={{ color: GOLD_HI, fontSize: 26, textShadow: `0 0 16px ${GOLD}` }}>
                YOU WIN
              </h2>
              <p className="font-['Cormorant_Garamond'] italic mt-2"
                style={{ color: '#FFF', fontSize: 15 }}>
                Successfully aligned all rows.
              </p>
              <div className="flex items-center justify-center gap-4 mt-3 font-['Outfit'] uppercase tracking-[0.22em] text-[10px]"
                style={{ color: `${GOLD}CC` }}>
                <span>Rows · <span style={{ color: GOLD_HI }}>{ROWS}</span></span>
                <span className="opacity-40">·</span>
                <span>Swaps · <span style={{ color: GOLD_HI }}>{swapCount}</span></span>
              </div>
              <button
                onClick={() => { setCycleComplete(false); changeCategory(); }}
                className="mt-5 w-full rounded-full py-3 font-['Outfit'] uppercase tracking-[0.24em] text-[12px] active:scale-95 transition-transform"
                style={{
                  background: `linear-gradient(180deg, ${GOLD_HI}, ${GOLD})`,
                  color: '#1a0a05',
                  boxShadow: `0 0 22px ${GOLD}88`,
                  border: `1px solid ${GOLD_HI}`,
                }}
                data-testid="harmony-complete-next">
                → Next Set
              </button>
              <button
                onClick={() => { setCycleComplete(false); performShuffle(false, iconSet); }}
                className="mt-2 w-full rounded-full py-3 font-['Outfit'] uppercase tracking-[0.24em] text-[11px] active:scale-95 transition-transform"
                style={{
                  background: 'transparent',
                  color: GOLD_HI,
                  border: `1px solid ${GOLD}88`,
                }}
                data-testid="harmony-complete-reshuffle">
                ↻ Reshuffle
              </button>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  );
};

export const HarmonyTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.6))' }}>
    <defs>
      <linearGradient id="harmonyIcon" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0" stopColor="#F5D97A"/>
        <stop offset="0.6" stopColor="#D4AF37"/>
        <stop offset="1" stopColor="#8B6B1F"/>
      </linearGradient>
    </defs>
    <rect x="4"  y="4"  width="5" height="5" rx="1" fill="url(#harmonyIcon)" opacity="0.9"/>
    <rect x="10" y="4"  width="5" height="5" rx="1" fill="url(#harmonyIcon)" opacity="0.4"/>
    <rect x="16" y="4"  width="5" height="5" rx="1" fill="url(#harmonyIcon)" opacity="0.7"/>
    <rect x="4"  y="10" width="5" height="5" rx="1" fill="url(#harmonyIcon)" opacity="0.4"/>
    <rect x="10" y="10" width="5" height="5" rx="1" fill="url(#harmonyIcon)"/>
    <rect x="16" y="10" width="5" height="5" rx="1" fill="url(#harmonyIcon)" opacity="0.4"/>
    <rect x="4"  y="16" width="5" height="5" rx="1" fill="url(#harmonyIcon)" opacity="0.7"/>
    <rect x="10" y="16" width="5" height="5" rx="1" fill="url(#harmonyIcon)" opacity="0.4"/>
    <rect x="16" y="16" width="5" height="5" rx="1" fill="url(#harmonyIcon)" opacity="0.9"/>
  </svg>
);

export default TheHarmony;
